// Considered: `StatusSparklineAtom` (`gap=6`, wide `size`) — the audit originally called for
// reusing it as-is, but its coverage blocks are fixed-size squares with one strip-level `onClick`;
// the wireframe needs flex-grown blocks with PER-SEGMENT inspection (each deviant opens its own
// staleness payload). Composed instead from `InspectableMolecule` + tokens, which already supports
// per-item click — no new atom, and `StatusSparklineAtom` is untouched for its existing callers.
// specs/stream-trajectory-tools.md Phase 5 note: audit row updated to reflect this composition.
//
// Source: design/metaspace-tools/metaspace-trajectory-v2.jsx:210-236 (FRESH_BLOCKS + deviant line).

import React from 'react';
import { useTranslation } from 'localization';
import InspectableMolecule from '../Inspectable/InspectableMolecule';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import type { InspectorPayload } from 'types/inspector';
import type { TrajectoryFreshnessSummary } from 'types/trajectory';
import './FreshnessCardMolecule.scss';

export interface FreshnessCoverageBlock {
  aspectSystemName: string;
  label: string;
  fresh: boolean;
  ageDays: number;
}

export interface FreshnessCardMoleculeProps {
  freshness: TrajectoryFreshnessSummary;
  coverage: FreshnessCoverageBlock[];
  buildBlockPayload: (block: FreshnessCoverageBlock) => InspectorPayload;
  buildStalenessPayload: (deviant: TrajectoryFreshnessSummary['stale'][number]) => InspectorPayload;
  onInspect?: (payload: InspectorPayload) => void;
  className?: string;
}

/**
 * Renders only the card BODY (coverage blocks + deviant line). The "N of M fresh" summary and its
 * `coverage`-kind inspect target are the enclosing `TrackCardMolecule`'s title/value/titlePayload
 * — matching the wireframe's `onTitle` handler on the card itself, not a second summary here.
 */
const FreshnessCardMolecule: React.FC<FreshnessCardMoleculeProps> = ({
  freshness,
  coverage,
  buildBlockPayload,
  buildStalenessPayload,
  onInspect,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div className={['freshness-card-molecule', className].filter(Boolean).join(' ')}>
      <div className="freshness-card-molecule__blocks">
        {coverage.map((block) => (
          <InspectableMolecule
            key={block.aspectSystemName}
            payload={buildBlockPayload(block)}
            onInspect={onInspect}
            peek={`${block.label} · ${block.fresh ? t('trajectory.freshness.fresh', { defaultValue: 'fresh' }) : t('trajectory.freshness.stale', { defaultValue: 'stale' })} ${block.ageDays}d`}
            block
            grow={1}
            aria-label={`${block.label} ${block.fresh ? 'fresh' : 'stale'}`}
            className={['freshness-card-molecule__block', block.fresh && 'freshness-card-molecule__block--fresh'].filter(Boolean).join(' ')}
          >
            <span className="freshness-card-molecule__block-fill" />
          </InspectableMolecule>
        ))}
      </div>

      {freshness.stale.length > 0 && (
        <div className="freshness-card-molecule__deviants">
          <span className="freshness-card-molecule__deviants-label">
            <GlyphAtom glyph="lucide:alert-triangle" />
            {t('trajectory.freshness.staleLabel', { defaultValue: 'stale:' })}
          </span>
          {freshness.stale.map((deviant) => (
            <InspectableMolecule
              key={deviant.aspectSystemName}
              payload={buildStalenessPayload(deviant)}
              onInspect={onInspect}
              peek={t('trajectory.freshness.stalePeek', { defaultValue: 'Past its revalidation window — request an update' })}
              aria-label={`${deviant.label} stale`}
            >
              <span className="freshness-card-molecule__deviant">
                {deviant.label} <strong>{deviant.ageDays}d</strong>
              </span>
            </InspectableMolecule>
          ))}
        </div>
      )}
    </div>
  );
};

FreshnessCardMolecule.displayName = 'FreshnessCardMolecule';

export default FreshnessCardMolecule;
